import React, { useEffect, useState } from "react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Loader2 } from "lucide-react"
import { useToast } from "src/components/ui/toast"
import { fetchWinLoss, exportUrl } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, formatINR, formatNumber } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import { WinLossDonut, LossReasonsTable } from "src/features/analytics/components/WinLossDonut"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"

export default function WinLossReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("30d")
  const [filters, setFilters] = useState(presetToRange("30d"))
  const { data, loading, fetch } = useReport(fetchWinLoss)

  useEffect(() => {
    fetch(filters).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to, filters.project_id, filters.source])

  const totals = data?.totals

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
        exportUrl={exportUrl("win_loss", filters)}
        showSourceFilter
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !totals || (totals.won === 0 && totals.lost === 0) ? (
        <EmptyReportState message="No won or lost deals in this period." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard label="Won" value={formatNumber(totals.won)} />
            <KpiCard label="Lost" value={formatNumber(totals.lost)} />
            <KpiCard label="Win Rate" value={`${totals.win_rate}%`} />
            <KpiCard label="Value Won" value={formatINR(totals.value_won)} />
          </div>

          <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <WinLossDonut totals={totals} />
            <LossReasonsTable reasons={data.loss_reasons} />
          </div>

          {/* By source */}
          {data.by_source?.length > 0 && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">By Source</div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">Source</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Won</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Lost</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Win Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_source.map((row) => (
                    <TableRow key={row.source}>
                      <TableCell className="py-2.5 text-slate-900">{row.label}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.won}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.lost}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.win_rate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* By rep */}
          {data.by_rep?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">By Rep</div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">Rep</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Won</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Lost</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Win Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_rep.map((row) => (
                    <TableRow key={row.user.id}>
                      <TableCell className="py-2.5 text-slate-900">{row.user.name}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.won}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.lost}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.win_rate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
